// ---------------------------------------------------------------------------
// Common Ground Farm & Coop project page (/our-work/common-ground).
// Owner brief, October 2026; updated per the 26 October website update brief:
// presented as an EMERGING PROJECT CONCEPT. Do not imply land has been
// acquired, membership is open, approvals are secured or investment is being
// accepted. The name is a working project name - not trademark-registered or
// legally cleared. Imagery is illustrative, not an acquired property.
// ---------------------------------------------------------------------------

export const commonGround = {
  name: "Common Ground Farm & Coop",
  tagline: "A place to grow together.",
  status: "Emerging project concept",
  overview: [
    "Common Ground Farm & Coop is an emerging project exploring how a regenerative shared farm and community cooperative could support independent enterprises, food growing and collective care for land.",
    "The concept brings together resident and nonresident participation, shared infrastructure, a community market garden and opportunities to participate in the produce and life of the farm.",
    "Development will test the practical foundations: suitable land, participant demand, enterprise viability, ownership and governance options, infrastructure costs and ongoing stewardship responsibilities.",
  ],
  participationHeading: "Participation",
  participationBody:
    "The concept welcomes resident and nonresident participation. Members could contribute as growers, enterprise operators, educators, volunteers or supporters, without needing to live on the farm. Any future residences and private spaces would be distinct from shared community areas, subject to the property, approvals and agreed arrangements.",

  gardenHeading: "The Community Market Garden",
  gardenBody: [
    "At the heart of Common Ground is intended to be a productive market garden where members and local residents could participate in growing, harvesting and sharing fresh, seasonal produce.",
    "The garden would provide opportunities for practical involvement, learning and access to locally grown food.",
    "The market garden would also create opportunities for paid employment and small agricultural enterprises as the cooperative develops.",
    "Produce-sharing and participation arrangements would be established collaboratively to ensure the garden remains productive, equitable and financially sustainable.",
  ],
  gardenActivitiesLabel: "Potential activities",
  gardenActivities: [
    "Produce boxes",
    "Farm-gate sales",
    "Seed saving",
    "Workshops",
    "Value-add food products",
    "Seasonal harvest gatherings",
    "Community-supported agriculture",
    "Orchard management and packing",
  ],

  enterprisesHeading: "Independent Enterprises and Shared Infrastructure",
  enterprisesBody: [
    "Common Ground is designed to support independent livelihoods within a cooperative environment.",
    "Participants could operate businesses in regenerative agriculture, horticulture, seed production, food processing, ecological restoration, education, creative practice and other complementary fields.",
    "Each enterprise would retain its independence while benefiting from access to shared resources.",
  ],
  infraLabel: "Depending on the property and community needs, shared infrastructure could include:",
  infrastructure: [
    "Market gardens, orchards and food forests",
    "Nurseries, greenhouses and seed propagation facilities",
    "Agricultural equipment, irrigation and water systems",
    "Workshops, tools and fabrication spaces",
    "Food processing, commercial kitchen and storage facilities",
    "Farm-gate retail and produce distribution",
    "Community gathering and educational spaces",
    "Renewable energy and ecological restoration infrastructure",
  ],
  infraNote:
    "The objective is to reduce duplication, make productive infrastructure more accessible and create opportunities for cooperation between enterprises.",
  diagramTitle: "How it fits together",
  diagramNodes: [
    { title: "The market garden" },
    { title: "Shared infrastructure" },
    { title: "Independent enterprises" },
    { title: "The wider community" },
  ],
  diagramNote: "",

  stewardshipHeading: "Regenerative Land Stewardship",
  stewardshipBody: [
    "Ecological restoration and long-term land health would guide the development of Common Ground.",
    "The farm would explore regenerative and organic agriculture, soil improvement, biodiversity conservation, water stewardship, native seed systems, perennial food production and habitat restoration.",
    "Food production and ecological regeneration would be integrated so that productive activity contributes to the health and resilience of the landscape.",
  ],

  governanceHeading: "Cooperative Ownership and Governance",
  governanceBody: [
    "Bioregional Ventures is exploring community land trusts, cooperative ownership and other long-term stewardship arrangements to protect the land's purpose for future generations.",
    "The intention is to establish a transparent, participatory governance structure that balances individual independence with collective responsibility.",
    "Members would contribute to the shared resources and stewardship of the property through agreed arrangements.",
    "The cooperative should ultimately function as an enduring community institution rather than depend on a single founder.",
  ],

  visionHeading: "The Vision",
  visionStatement: "Independent lives. Productive land. Shared infrastructure. Collective stewardship.",

  developmentHeading: "Current Development",
  developmentBody:
    "Common Ground Farm & Coop is an emerging project concept of Bioregional Ventures, currently in the visioning and development stage.",
  developmentLabel: "Current areas of exploration include:",
  developmentAreas: [
    "Suitable properties",
    "Community partnerships",
    "Cooperative governance",
    "Regenerative food production",
    "Shared infrastructure models",
  ],

  ctaHeading: "Help Grow Common Ground",
  ctaBody:
    "We welcome conversations with growers, landholders, prospective cooperative members, regenerative enterprises and people interested in contributing to a shared vision for land, food and community.",
  ctaLabel: "Enquire about Common Ground",
  ctaHref: "/contact?topic=common-ground",
};

// Card shown in Our Work → Active projects.
export const activeProjects = {
  eyebrow: "Active projects",
  projects: [
    {
      name: commonGround.name,
      tagline: commonGround.tagline,
      status: "Emerging project concept",
      href: "/our-work/common-ground",
      image: {
        src: "/common-ground-farm.jpg",
        alt: "A tractor gathering hay on lush green fields in a tranquil rural landscape.",
      },
    },
  ],
};
